/**
 * FOC Lead Popup engine
 * Triggers: 8s, ~30% scroll, or exit intent (first wins).
 * Close or submit suppresses 1 day. Inactivity re-fire stays off unless a section turns it on.
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'foc_popup_';
  var SUBMIT_DAYS = 1;
  var DISMISS_DAYS = 1;
  var FIRST_DELAY_MS = 8000;
  var SCROLL_RATIO = 0.3;
  var INACTIVITY_MS = 90000;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function storageKey(popupId, kind) {
    return STORAGE_PREFIX + popupId + '_' + kind;
  }

  function readJson(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota */
    }
  }

  function isSubmitSuppressed(popupId) {
    var data = readJson(storageKey(popupId, 'submitted'), null);
    if (!data || !data.at) return false;
    var age = Date.now() - data.at;
    return age < SUBMIT_DAYS * 24 * 60 * 60 * 1000;
  }

  function markSubmitted(popupId) {
    writeJson(storageKey(popupId, 'submitted'), { at: Date.now() });
  }

  function isDismissSuppressed(popupId) {
    var data = readJson(storageKey(popupId, 'dismissed'), null);
    if (!data || !data.at) return false;
    var age = Date.now() - data.at;
    return age < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  }

  function markDismissed(popupId) {
    writeJson(storageKey(popupId, 'dismissed'), { at: Date.now() });
  }

  function pushEvent(name, payload) {
    var params = payload || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params));
    // dataLayer alone does not reach GA4. The Google tag on the page does.
    sendGa4Event(name, params, 8);
  }

  function sendGa4Event(name, params, attemptsLeft) {
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', name, params);
      } catch (e) {
        /* analytics must not block the popup */
      }
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(function () {
      sendGa4Event(name, params, attemptsLeft - 1);
    }, 500);
  }

  function FocLeadPopup(root) {
    this.root = root;
    var cfgEl = root.querySelector('[data-foc-popup-config]');
    this.config = this.parseConfig(cfgEl ? cfgEl.textContent : null);
    if (!this.config || !this.config.enabled) return;
    if (!this.config.popupId) return;
    if (isSubmitSuppressed(this.config.popupId) || isDismissSuppressed(this.config.popupId)) return;

    this.overlay = $('[data-foc-popup-overlay]', root);
    this.dialog = $('[data-foc-popup-dialog]', root);
    this.closeBtns = $all('[data-foc-popup-close]', root);
    this.panels = {
      capture: $('[data-foc-popup-panel="capture"]', root),
      segment: $('[data-foc-popup-panel="segment"]', root),
      step2: $('[data-foc-popup-panel="step2"]', root),
      goal: $('[data-foc-popup-panel="goal"]', root),
      success: $('[data-foc-popup-panel="success"]', root),
    };
    this.emailInputs = $all('[data-foc-popup-email]', root);
    this.forms = $all('[data-foc-popup-form]', root);
    this.errorEls = $all('[data-foc-popup-error]', root);
    this.step2Sub = $('[data-foc-popup-step2-sub]', root);
    this.stepPill = $('[data-foc-popup-segment-pill]', root);
    this.successCta = $('[data-foc-popup-success-cta]', root);
    this.submitBtns = $all('[data-foc-popup-submit]', root);

    this.open = false;
    this.showsThisPageview = 0;
    this.selectedSegment = null;
    this.goalCheckoutUrl = '';
    this.submitting = false;
    this.inactivityTimer = null;
    this.firstTimer = null;
    this.youtubePlaying = false;
    this.inactivityArmed = false;
    this.sessionDismissedMax = false;
    this.pendingDelay = null;
    this._onScroll = null;
    this._onExit = null;

    this.bindUi();
    this.bindYoutubePause();
    this.armTriggers();
  }

  FocLeadPopup.prototype.parseConfig = function (raw) {
    try {
      if (!raw) return null;
      return JSON.parse(String(raw).trim());
    } catch (e) {
      return null;
    }
  };

  FocLeadPopup.prototype.analyticsBase = function () {
    var base = {
      popup_id: this.config.popupId,
      page_variant: this.config.pageVariant || '',
      esp_tag: this.config.espTag || '',
    };
    if (this.selectedSegment) base.segment = this.selectedSegment.key;
    return base;
  };

  FocLeadPopup.prototype.bindUi = function () {
    var self = this;

    this.closeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.dismiss('close_button');
      });
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.dismiss('overlay');
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!self.open) return;
      if (e.key === 'Escape') self.dismiss('escape');
    });

    $all('[data-foc-popup-segment]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.selectedSegment = {
          key: btn.getAttribute('data-key'),
          label: btn.getAttribute('data-label') || '',
        };
        self.showStep2();
      });
    });

    $all('[data-foc-popup-goal]', this.root).forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href') || '';
        if (!href) return;
        e.preventDefault();
        self.selectedSegment = {
          key: link.getAttribute('data-key'),
          label: link.getAttribute('data-label') || '',
        };
        self.goalCheckoutUrl = href;
        pushEvent('popup_goal_selected', self.analyticsBase());
        self.showGoalEmail();
      });
    });

    var goalBack = $('[data-foc-popup-goal-back]', this.root);
    if (goalBack) {
      goalBack.addEventListener('click', function () {
        self.showGoalChoices();
      });
    }

    var back = $('[data-foc-popup-back]', this.root);
    if (back) {
      back.addEventListener('click', function () {
        self.selectedSegment = null;
        self.showPanel('segment');
      });
    }

    this.forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.handleSubmit(form);
      });
    });

    if (this.successCta) {
      this.successCta.addEventListener('click', function (e) {
        var href = self.successCta.getAttribute('href') || '';
        // Always close the modal; keep default navigation for real URLs / hash anchors.
        self.hide(false);
        if (!href || href === '#') {
          e.preventDefault();
        }
      });
    }
  };

  FocLeadPopup.prototype.showPanel = function (name) {
    var self = this;
    Object.keys(this.panels).forEach(function (key) {
      var el = self.panels[key];
      if (!el) return;
      if (key === name) el.hidden = false;
      else el.hidden = true;
    });
  };

  FocLeadPopup.prototype.showGoalEmail = function () {
    var choices = $('[data-foc-popup-goal-choices]', this.root);
    var emailStep = $('[data-foc-popup-goal-email]', this.root);
    var picked = $('[data-foc-popup-goal-picked]', this.root);
    if (choices) choices.hidden = true;
    if (emailStep) emailStep.hidden = false;
    if (picked && this.selectedSegment) picked.textContent = this.selectedSegment.label;
    this.clearErrors();
    var input = emailStep ? $('[data-foc-popup-email]', emailStep) : null;
    if (input) input.focus();
  };

  FocLeadPopup.prototype.showGoalChoices = function () {
    var choices = $('[data-foc-popup-goal-choices]', this.root);
    var emailStep = $('[data-foc-popup-goal-email]', this.root);
    if (emailStep) emailStep.hidden = true;
    if (choices) choices.hidden = false;
    this.clearErrors();
  };

  FocLeadPopup.prototype.showStep2 = function () {
    if (this.stepPill && this.selectedSegment) {
      this.stepPill.textContent = this.selectedSegment.label;
    }
    if (this.step2Sub && this.config.step2SubheadTemplate && this.selectedSegment) {
      this.step2Sub.textContent = this.config.step2SubheadTemplate.replace(
        '{SEGMENT}',
        this.selectedSegment.label
      );
    }
    this.showPanel('step2');
  };

  FocLeadPopup.prototype.clearErrors = function () {
    this.errorEls.forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
  };

  FocLeadPopup.prototype.showError = function (message) {
    var visiblePanel = $('[data-foc-popup-panel]:not([hidden])', this.root);
    var err = visiblePanel ? $('[data-foc-popup-error]', visiblePanel) : null;
    if (!err) err = this.errorEls[0];
    if (err) {
      err.hidden = false;
      err.textContent = message;
    }
  };

  FocLeadPopup.prototype.setSubmitting = function (on) {
    this.submitting = on;
    this.submitBtns.forEach(function (btn) {
      btn.disabled = on;
      var label = btn.getAttribute(on ? 'data-label-loading' : 'data-label-idle');
      if (label) {
        var textEl = btn.querySelector('[data-foc-popup-submit-label]');
        if (textEl) textEl.textContent = label;
      }
    });
  };

  FocLeadPopup.prototype.handleSubmit = function (form) {
    var self = this;
    if (this.submitting) return;

    var input = $('[data-foc-popup-email]', form) || this.emailInputs[0];
    var email = input && input.value ? String(input.value).trim() : '';
    if (!email) {
      this.showError('Please enter your email.');
      return;
    }

    this.clearErrors();
    this.setSubmitting(true);

    this.subscribeKlaviyo(email)
      .then(function () {
        pushEvent('popup_submitted', self.analyticsBase());
        markSubmitted(self.config.popupId);
        self.setSubmitting(false);
        self.onSubmitSuccess();
      })
      .catch(function (err) {
        self.setSubmitting(false);
        self.showError((err && err.message) || 'Something went wrong. Please try again.');
      });
  };

  FocLeadPopup.prototype.subscribeKlaviyo = function (email) {
    var companyId = this.config.klaviyoCompanyId;
    var listId = this.config.klaviyoListId;
    if (!companyId) {
      return Promise.reject(new Error('Klaviyo is not configured yet.'));
    }
    if (!listId) {
      return Promise.reject(new Error('Klaviyo list ID is missing.'));
    }

    var properties = {
      foc_popup_id: this.config.popupId,
      foc_page_variant: this.config.pageVariant || '',
      foc_esp_tag: this.config.espTag || '',
    };
    if (this.selectedSegment) {
      properties.foc_segment = this.selectedSegment.key;
      properties.foc_segment_label = this.selectedSegment.label;
      properties.foc_esp_tag_segment = 'segment-' + this.selectedSegment.key;
    }

    var revision = this.config.klaviyoRevision || '2024-10-15';
    var subscriptionUrl =
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId);
    var eventUrl =
      'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId);

    var subscriptionBody = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'FOC Lead Popup',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    var eventBody = {
      data: {
        type: 'event',
        attributes: {
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'FOC Popup Submitted' },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties,
              },
            },
          },
          properties: Object.assign(
            {
              popup_id: this.config.popupId,
              page_variant: this.config.pageVariant || '',
              esp_tag: this.config.espTag || '',
            },
            this.selectedSegment
              ? {
                  segment: this.selectedSegment.key,
                  segment_label: this.selectedSegment.label,
                }
              : {}
          ),
        },
      },
    };

    var headers = {
      'Content-Type': 'application/json',
      revision: revision,
    };

    return fetch(subscriptionUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(subscriptionBody),
    }).then(function (res) {
      // 202 / 200 / 204 are success; some accounts return 202 Accepted
      if (!(res.status === 202 || res.status === 200 || res.status === 204)) {
        return res.text().then(function (text) {
          throw new Error(text || 'Subscribe failed (' + res.status + ')');
        });
      }
      // Fire metric for Flows (non-blocking if it fails)
      return fetch(eventUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(eventBody),
      }).then(function () {
        return true;
      }).catch(function () {
        return true;
      });
    });
  };

  FocLeadPopup.prototype.onSubmitSuccess = function () {
    var variant = this.config.variant;

    if (variant === 'goal-checkout' && this.goalCheckoutUrl) {
      this.sessionDismissedMax = true;
      this.clearInactivity();
      window.location.href = this.goalCheckoutUrl;
      return;
    }

    if (variant === 'lead-magnet' && this.config.pdfDownloadUrl) {
      this.triggerDownload(this.config.pdfDownloadUrl);
    }

    if (this.panels.success) {
      this.showPanel('success');
    } else {
      this.hide(false);
    }

    if (
      (variant === 'gift-code' ||
        this.config.pageVariant === 'lite' ||
        this.config.pageVariant === 'premium') &&
      window.FOCFreeGift &&
      typeof window.FOCFreeGift.setClaim === 'function'
    ) {
      window.FOCFreeGift.setClaim('popup');
    }

    // Stop further shows this session after submit
    this.sessionDismissedMax = true;
    this.clearInactivity();
  };

  FocLeadPopup.prototype.triggerDownload = function (url) {
    try {
      var a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', '');
      a.setAttribute('target', '_blank');
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.open(url, '_blank');
    }
  };

  FocLeadPopup.prototype.canShow = function () {
    if (this.sessionDismissedMax) return false;
    if (isSubmitSuppressed(this.config.popupId)) return false;
    if (isDismissSuppressed(this.config.popupId)) return false;
    if (this.showsThisPageview >= 2) return false;
    if (this.open) return false;
    return true;
  };

  FocLeadPopup.prototype.reveal = function (reason) {
    if (!this.canShow()) return;
    this.cancelPendingTriggers();
    this.showsThisPageview += 1;
    this.open = true;
    this.root.hidden = false;
    document.body.classList.add('foc-popup-open');
    if (this.dialog) this.dialog.scrollTop = 0;
    var bodyEl = this.root.querySelector('.foc-popup__body');
    if (bodyEl) bodyEl.scrollTop = 0;

    if (this.config.variant === 'goal-checkout') {
      this.showPanel('goal');
    } else if (this.config.variant === 'segment-gift' && !this.selectedSegment) {
      this.showPanel('segment');
    } else if (this.config.variant === 'segment-gift' && this.selectedSegment) {
      this.showStep2();
    } else {
      this.showPanel('capture');
    }

    pushEvent('popup_shown', Object.assign(this.analyticsBase(), { reason: reason || 'initial' }));

    if (this.showsThisPageview >= 2) {
      this.sessionDismissedMax = true;
      this.clearInactivity();
    }
  };

  FocLeadPopup.prototype.hide = function (trackDismiss) {
    if (!this.open) return;
    this.open = false;
    this.root.hidden = true;
    document.body.classList.remove('foc-popup-open');
    if (trackDismiss) {
      pushEvent('popup_dismissed', this.analyticsBase());
    }
  };

  FocLeadPopup.prototype.dismiss = function () {
    this.hide(true);
    markDismissed(this.config.popupId);
    this.sessionDismissedMax = true;
    this.clearInactivity();
    this.cancelPendingTriggers();
  };

  FocLeadPopup.prototype.cancelPendingTriggers = function () {
    if (this.pendingDelay) {
      clearTimeout(this.pendingDelay);
      this.pendingDelay = null;
    }
    if (this.firstTimer) {
      clearTimeout(this.firstTimer);
      this.firstTimer = null;
    }
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
    if (this._onExit) {
      document.removeEventListener('mouseout', this._onExit);
      this._onExit = null;
    }
  };

  FocLeadPopup.prototype.tryReveal = function (reason) {
    if (!this.canShow()) return;
    this.reveal(reason);
  };

  FocLeadPopup.prototype.armTriggers = function () {
    var self = this;
    var delay = typeof this.config.firstDelayMs === 'number' ? this.config.firstDelayMs : FIRST_DELAY_MS;

    this.pendingDelay = setTimeout(function () {
      self.tryReveal('delay');
    }, delay);

    this._onScroll = function () {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= SCROLL_RATIO) {
        self.tryReveal('scroll');
      }
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    this._onExit = function (e) {
      if (!e || e.clientY > 0) return;
      if (e.relatedTarget || e.toElement) return;
      self.tryReveal('exit');
    };
    document.addEventListener('mouseout', this._onExit);
  };

  FocLeadPopup.prototype.armInactivity = function () {
    if (!this.config.inactivityEnabled) return;
    if (this.showsThisPageview >= 2 || this.sessionDismissedMax) return;
    this.inactivityArmed = true;
    this.resetInactivityTimer();
    if (!this._activityBound) {
      this._activityBound = true;
      var self = this;
      var events = ['scroll', 'click', 'keydown', 'mousemove', 'touchstart'];
      this._onActivity = function () {
        if (!self.inactivityArmed || self.open) return;
        self.resetInactivityTimer();
      };
      events.forEach(function (evt) {
        window.addEventListener(evt, self._onActivity, { passive: true });
      });
    }
  };

  FocLeadPopup.prototype.resetInactivityTimer = function () {
    var self = this;
    if (!this.inactivityArmed) return;
    this.clearInactivityTimerOnly();
    if (this.youtubePlaying) return;
    var ms = typeof this.config.inactivityMs === 'number' ? this.config.inactivityMs : INACTIVITY_MS;
    this.inactivityTimer = setTimeout(function () {
      if (!self.canShow()) return;
      self.inactivityArmed = false;
      self.reveal('inactivity');
    }, ms);
  };

  FocLeadPopup.prototype.clearInactivityTimerOnly = function () {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  };

  FocLeadPopup.prototype.clearInactivity = function () {
    this.inactivityArmed = false;
    this.clearInactivityTimerOnly();
  };

  FocLeadPopup.prototype.bindYoutubePause = function () {
    var self = this;
    // Listen for YouTube IFrame API postMessage events
    window.addEventListener('message', function (event) {
      if (!event.data) return;
      var data = event.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch (e) {
        return;
      }
      if (!data || data.event !== 'onStateChange') return;
      // YT.PlayerState.PLAYING = 1, PAUSED = 2, ENDED = 0, BUFFERING = 3
      var state = data.info;
      if (state === 1 || state === 3) {
        self.youtubePlaying = true;
        self.clearInactivityTimerOnly();
      } else {
        self.youtubePlaying = false;
        if (self.inactivityArmed && !self.open) self.resetInactivityTimer();
      }
    });

    // Enable JS API on existing YouTube iframes so they emit events
    $all('iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"]').forEach(
      function (iframe) {
        try {
          var src = iframe.getAttribute('src') || '';
          if (src.indexOf('enablejsapi=1') === -1) {
            var join = src.indexOf('?') === -1 ? '?' : '&';
            iframe.setAttribute('src', src + join + 'enablejsapi=1');
          }
        } catch (e) {
          /* ignore */
        }
      }
    );
  };

  function init() {
    $all('[data-foc-lead-popup]').forEach(function (el) {
      if (el.__focLeadPopup) return;
      el.__focLeadPopup = new FocLeadPopup(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
